import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Crown, 
  Check, 
  Zap, 
  Users, 
  Target, 
  MessageSquare,
  BarChart3,
  Loader2,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
  Clock,
  Mail,
  Bell,
  BellOff,
  Ticket,
  Gift,
  Calendar,
  Link2,
  Unlink,
  RefreshCw,
  Settings,
  CalendarDays,
  Headphones,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { plansApi, subscriptionApi, promoApi, calendarApi } from '../lib/api';
import { toast } from 'sonner';

const PLAN_FEATURES = {
  free: {
    name: 'Free Trial',
    price: 0,
    annualPrice: 0,
    description: '30-day trial to get started',
    features: [
      { text: '1 Goal (unlimited action items)', included: true },
      { text: '5 Tasks', included: true },
      { text: '5 AI Coach messages/day', included: true },
      { text: '30-day trial period', included: true },
      { text: 'Teams', included: false },
      { text: 'Strategic Scorecard', included: false },
      { text: 'Weekly AI Summary', included: false },
    ],
    highlight: false,
  },
  pro: {
    name: 'Pro',
    price: 19,
    annualPrice: 209, // 19 * 11 (1 month free)
    description: 'For growing teams and professionals',
    features: [
      { text: '20 Goals', included: true },
      { text: '100 Tasks', included: true },
      { text: '5 Teams (10 members each)', included: true },
      { text: '50 AI Coach messages/day', included: true },
      { text: 'Strategic Scorecard', included: true },
      { text: 'Weekly AI Summary', included: true },
      { text: 'Priority Support', included: false },
    ],
    highlight: true,
  },
  enterprise: {
    name: 'Enterprise',
    price: 49,
    annualPrice: 539, // 49 * 11 (1 month free)
    description: 'For organizations that need more',
    features: [
      { text: 'Vision, Mission & Strategic Priorities', included: true },
      { text: 'Goals linked to Priorities', included: true },
      { text: 'Strategy Map Visualization', included: true },
      { text: 'Unlimited Goals', included: true },
      { text: 'Unlimited Tasks', included: true },
      { text: 'Unlimited Teams & Members', included: true },
      { text: 'Unlimited AI Coach messages', included: true },
      { text: 'Strategic Scorecard', included: true },
      { text: 'Weekly AI Summary', included: true },
      { text: 'Priority Support', included: true },
    ],
    highlight: false,
  },
};

const Billing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [emailNotificationsDisabled, setEmailNotificationsDisabled] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [promoInfo, setPromoInfo] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  
  // Google Calendar state
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const isAdmin = user?.is_admin || false;

  // Check for returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    
    // Check for Google Calendar connection result
    const calendarConnected = searchParams.get('calendar_connected');
    const calendarError = searchParams.get('calendar_error');
    
    if (calendarConnected === 'true') {
      toast.success('Google Calendar connected successfully!');
      navigate('/billing', { replace: true });
      loadCalendarStatus();
    } else if (calendarError) {
      toast.error(`Failed to connect Google Calendar: ${calendarError}`);
      navigate('/billing', { replace: true });
    }
  }, [searchParams]);

  // Load usage data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [usageRes, historyRes] = await Promise.all([
          plansApi.getMyUsage(),
          subscriptionApi.getHistory(),
        ]);
        setUsage(usageRes.data);
        setHistory(historyRes.data);
        setEmailNotificationsDisabled(usageRes.data.email_notifications_disabled || false);
        
        // Check if user has active promo
        if (usageRes.data.promo_expires_at) {
          setPromoInfo({
            plan: usageRes.data.promo_plan,
            expires_at: usageRes.data.promo_expires_at,
            code: usageRes.data.promo_code_used
          });
        }
      } catch (err) {
        console.error('Failed to load usage data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    loadCalendarStatus();
  }, [paymentResult]);

  // Load Google Calendar status
  const loadCalendarStatus = async () => {
    try {
      setCalendarLoading(true);
      const res = await calendarApi.getStatus();
      setCalendarStatus(res.data);
    } catch (err) {
      console.error('Failed to load calendar status:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Connect Google Calendar
  const handleConnectCalendar = async () => {
    try {
      const res = await calendarApi.connect();
      // Redirect to Google OAuth
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start calendar connection');
    }
  };

  // Disconnect Google Calendar
  const handleDisconnectCalendar = async () => {
    setDisconnecting(true);
    try {
      await calendarApi.disconnect();
      setCalendarStatus({ connected: false, settings: null });
      toast.success('Google Calendar disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect calendar');
    } finally {
      setDisconnecting(false);
    }
  };

  // Sync all to Google Calendar
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await calendarApi.syncAll();
      toast.success(`Synced ${res.data.synced_goals} goals and ${res.data.synced_tasks} tasks to Google Calendar`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync to calendar');
    } finally {
      setSyncing(false);
    }
  };

  // Update calendar settings
  const handleUpdateCalendarSettings = async (key, value) => {
    try {
      const res = await calendarApi.updateSettings({ [key]: value });
      setCalendarStatus(prev => ({
        ...prev,
        settings: res.data.settings
      }));
      toast.success('Calendar settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    setRedeemingPromo(true);
    try {
      const res = await promoApi.redeem(promoCode.trim());
      toast.success(res.data.message);
      setPromoCode('');
      // Reload usage to reflect new plan
      const usageRes = await plansApi.getMyUsage();
      setUsage(usageRes.data);
      if (usageRes.data.promo_expires_at) {
        setPromoInfo({
          plan: usageRes.data.promo_plan,
          expires_at: usageRes.data.promo_expires_at,
          code: usageRes.data.promo_code_used
        });
      }
      // Reload page to update user context
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem promo code');
    } finally {
      setRedeemingPromo(false);
    }
  };

  const handleToggleEmailNotifications = async () => {
    setSavingPrefs(true);
    try {
      const newValue = !emailNotificationsDisabled;
      await plansApi.updateEmailPreferences(newValue);
      setEmailNotificationsDisabled(newValue);
      toast.success(newValue ? 'Email notifications disabled' : 'Email notifications enabled');
    } catch (err) {
      console.error('Failed to update email preferences:', err);
      toast.error('Failed to update email preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setPaymentResult({ success: false, message: 'Payment verification timed out. Please check your email for confirmation.' });
      // Clear the session_id from URL
      navigate('/billing', { replace: true });
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await subscriptionApi.getStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        setPaymentResult({ 
          success: true, 
          message: data.message || `Congratulations! You've been upgraded to ${data.plan?.charAt(0).toUpperCase() + data.plan?.slice(1)} plan!`
        });
        // Clear the session_id from URL and reload to get updated user data
        navigate('/billing', { replace: true });
        window.location.reload();
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        setPaymentResult({ success: false, message: 'Payment session expired. Please try again.' });
        // Clear the session_id from URL
        navigate('/billing', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setCheckingPayment(false);
        setPaymentResult({ success: false, message: 'Error checking payment status. Please contact support.' });
        // Clear the session_id from URL
        navigate('/billing', { replace: true });
      }
    }
  };

  const handleUpgrade = async (packageId) => {
    if (upgrading) return;
    
    setUpgrading(packageId);
    try {
      const response = await subscriptionApi.createCheckout(packageId, billingPeriod);
      if (response.data.checkout_url) {
        const checkoutUrl = response.data.checkout_url;
        
        // Try opening in new tab first (bypasses popup blockers better)
        const newWindow = window.open(checkoutUrl, '_blank');
        
        // If popup was blocked, fall back to redirect
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          toast.info('Redirecting to payment page...');
          // Use replace to avoid back button issues
          window.location.replace(checkoutUrl);
        } else {
          toast.success('Payment page opened in new tab');
          setUpgrading(null);
        }
      } else {
        setUpgrading(null);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(null);
    }
  };

  const currentPlan = user?.plan || 'free';
  const effectivePlan = isAdmin ? 'enterprise' : currentPlan;
  const planOrder = { free: 0, pro: 1, enterprise: 2 };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#26C6DA]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            Billing & Plans
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage your subscription and view usage
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full mr-2">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-300">Admin Access</span>
            </div>
          )}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            isAdmin ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-[#26C6DA]/10 border border-[#26C6DA]/30'
          }`}>
            <Crown className={`w-4 h-4 ${isAdmin ? 'text-purple-400' : 'text-[#26C6DA]'}`} />
            <span className={`text-sm font-medium capitalize ${isAdmin ? 'text-purple-300' : 'text-[#26C6DA]'}`}>
              {isAdmin ? 'Enterprise (Admin)' : `${currentPlan} Plan`}
            </span>
          </div>
        </div>
      </div>

      {/* Admin Banner */}
      {isAdmin && (
        <div className="bg-gradient-to-r from-purple-500/10 to-amber-500/10 border border-purple-500/30 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <Crown className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-300">Administrator Privileges</h3>
              <p className="text-sm text-purple-200/70 mt-1">
                As an admin, you have <strong>Enterprise-level access</strong> to all features regardless of your subscription plan. 
                This includes unlimited goals, tasks, teams, AI messages, Strategic Scorecard, and Weekly AI Summary.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Result Banner */}
      {checkingPayment && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6 flex items-center gap-4">
          <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
          <div>
            <p className="font-medium text-blue-300">Processing your payment...</p>
            <p className="text-sm text-blue-200/70">Please wait while we verify your payment.</p>
          </div>
        </div>
      )}

      {paymentResult && (
        <div className={`rounded-2xl p-6 flex items-center gap-4 ${
          paymentResult.success 
            ? 'bg-emerald-500/10 border border-emerald-500/30' 
            : 'bg-red-500/10 border border-red-500/30'
        }`}>
          {paymentResult.success ? (
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
          ) : (
            <XCircle className="w-6 h-6 text-red-400" />
          )}
          <div>
            <p className={`font-medium ${paymentResult.success ? 'text-emerald-300' : 'text-red-300'}`}>
              {paymentResult.success ? 'Payment Successful!' : 'Payment Issue'}
            </p>
            <p className={`text-sm ${paymentResult.success ? 'text-emerald-200/70' : 'text-red-200/70'}`}>
              {paymentResult.message}
            </p>
          </div>
        </div>
      )}

      {/* Current Usage */}
      {usage && (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-[#1F2937] p-6">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#26C6DA]" />
            Current Usage
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <UsageItem 
              icon={Target}
              label="Goals"
              current={usage.usage.goals}
              max={usage.limits.max_goals}
            />
            <UsageItem 
              icon={Check}
              label="Tasks"
              current={usage.usage.tasks}
              max={usage.limits.max_tasks}
            />
            <UsageItem 
              icon={Users}
              label="Teams Owned"
              current={usage.usage.teams_owned}
              max={usage.limits.max_teams}
            />
            <UsageItem 
              icon={MessageSquare}
              label="AI Messages Today"
              current={usage.usage.ai_messages_today}
              max={usage.limits.ai_coach_messages_per_day}
            />
          </div>
        </div>
      )}

      {/* Email Notification Preferences */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-[#1F2937] p-6" data-testid="email-preferences-section">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#26C6DA]" />
          Email Notifications
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${emailNotificationsDisabled ? 'bg-slate-200 dark:bg-slate-700' : 'bg-[#26C6DA]/10'}`}>
                {emailNotificationsDisabled ? (
                  <BellOff className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                ) : (
                  <Bell className="w-5 h-5 text-[#26C6DA]" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-800 dark:text-slate-100">
                  {emailNotificationsDisabled ? 'Notifications Disabled' : 'Notifications Enabled'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {emailNotificationsDisabled 
                    ? 'You will not receive daily prompts or weekly summary emails' 
                    : 'Receive daily reflection prompts and weekly AI summaries via email'}
                </p>
              </div>
            </div>
            <Button
              onClick={handleToggleEmailNotifications}
              disabled={savingPrefs}
              variant={emailNotificationsDisabled ? 'default' : 'outline'}
              className={`rounded-full ${
                emailNotificationsDisabled 
                  ? 'bg-[#26C6DA] hover:bg-[#0891B2] text-white' 
                  : 'border-slate-300 dark:border-[#1F2937] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#1A1A2E]'
              }`}
              data-testid="toggle-email-notifications-btn"
            >
              {savingPrefs ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : emailNotificationsDisabled ? (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Enable
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-2" />
                  Disable
                </>
              )}
            </Button>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 pl-4">
            <p className="mb-1"><strong>Daily Prompts:</strong> Sent every day at 7:00 AM UTC</p>
            <p><strong>Weekly Summary:</strong> AI-generated summary sent every Sunday at 8:00 AM UTC (Pro/Enterprise plans)</p>
          </div>
        </div>
      </div>

      {/* Google Calendar Integration */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-[#1F2937] p-6" data-testid="google-calendar-section">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#26C6DA]" />
          Google Calendar Integration
        </h2>
        
        {calendarLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#26C6DA]" />
          </div>
        ) : calendarStatus?.connected ? (
          <div className="space-y-4">
            {/* Connection Status */}
            <div className="flex items-center justify-between p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-100">Calendar Connected</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Connected on {new Date(calendarStatus.connected_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSyncAll}
                  disabled={syncing}
                  variant="outline"
                  className="rounded-full border-[#26C6DA] text-[#26C6DA] hover:bg-[#26C6DA]/10"
                  data-testid="sync-calendar-btn"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync All
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleDisconnectCalendar}
                  disabled={disconnecting}
                  variant="outline"
                  className="rounded-full border-red-400 text-red-400 hover:bg-red-500/10"
                  data-testid="disconnect-calendar-btn"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Sync Settings */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Sync Settings
              </h3>
              
              <div className="grid gap-3">
                {/* Sync Goals Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-lg border border-slate-200 dark:border-[#1F2937]">
                  <div className="flex items-center gap-3">
                    <Target className="w-4 h-4 text-[#8B5CF6]" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Sync Goals to Calendar</span>
                  </div>
                  <Button
                    onClick={() => handleUpdateCalendarSettings('sync_goals', !calendarStatus.settings?.sync_goals)}
                    variant="ghost"
                    size="sm"
                    className={`rounded-full px-4 ${
                      calendarStatus.settings?.sync_goals 
                        ? 'bg-[#26C6DA]/20 text-[#26C6DA]' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                    }`}
                    data-testid="toggle-sync-goals"
                  >
                    {calendarStatus.settings?.sync_goals ? 'On' : 'Off'}
                  </Button>
                </div>

                {/* Sync Tasks Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-lg border border-slate-200 dark:border-[#1F2937]">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-4 h-4 text-[#26C6DA]" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Sync Tasks to Calendar</span>
                  </div>
                  <Button
                    onClick={() => handleUpdateCalendarSettings('sync_tasks', !calendarStatus.settings?.sync_tasks)}
                    variant="ghost"
                    size="sm"
                    className={`rounded-full px-4 ${
                      calendarStatus.settings?.sync_tasks 
                        ? 'bg-[#26C6DA]/20 text-[#26C6DA]' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                    }`}
                    data-testid="toggle-sync-tasks"
                  >
                    {calendarStatus.settings?.sync_tasks ? 'On' : 'Off'}
                  </Button>
                </div>

                {/* Reminders Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-lg border border-slate-200 dark:border-[#1F2937]">
                  <div className="flex items-center gap-3">
                    <Bell className="w-4 h-4 text-amber-400" />
                    <div>
                      <span className="text-sm text-slate-700 dark:text-slate-300">Calendar Reminders</span>
                      <p className="text-xs text-slate-500 dark:text-slate-400">1 day before due date</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleUpdateCalendarSettings('reminders_enabled', !calendarStatus.settings?.reminders_enabled)}
                    variant="ghost"
                    size="sm"
                    className={`rounded-full px-4 ${
                      calendarStatus.settings?.reminders_enabled 
                        ? 'bg-[#26C6DA]/20 text-[#26C6DA]' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                    }`}
                    data-testid="toggle-reminders"
                  >
                    {calendarStatus.settings?.reminders_enabled ? 'On' : 'Off'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400 pl-4">
              <p className="mb-1"><strong>Auto-sync:</strong> Goals and tasks automatically sync when created or updated</p>
              <p><strong>Two-way:</strong> Import calendar events as tasks from the Calendar Events page</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-slate-200 dark:bg-slate-700 rounded-lg">
                  <Calendar className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-100">Calendar Not Connected</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Connect to sync goals and tasks to Google Calendar
                  </p>
                </div>
              </div>
              <Button
                onClick={handleConnectCalendar}
                className="rounded-full bg-[#26C6DA] hover:bg-[#0891B2] text-white"
                data-testid="connect-calendar-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect Google Calendar
              </Button>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 pl-4">
              <p className="mb-1"><strong>What gets synced:</strong> Goals and tasks with due dates appear in your Google Calendar</p>
              <p><strong>Two-way sync:</strong> Import events from your calendar as tasks or goals</p>
            </div>
          </div>
        )}
      </div>

      {/* Priority Support Section - Enterprise Only */}
      {(effectivePlan === 'enterprise') && (
        <div className="bg-gradient-to-r from-purple-500/10 to-[#26C6DA]/10 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-6" data-testid="priority-support-section">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
            <Headphones className="w-5 h-5 text-purple-400" />
            Priority Support
            <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">Enterprise</span>
          </h2>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              As an Enterprise user, you have access to priority support with faster response times.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Email Support */}
              <a 
                href="mailto:support@strategicarchitect.net?subject=Priority%20Support%20Request%20-%20Enterprise"
                className="flex items-center gap-4 p-4 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-xl border border-purple-500/20 hover:border-purple-500/40 transition-colors group"
                data-testid="priority-support-email"
              >
                <div className="p-3 bg-purple-500/20 rounded-lg group-hover:bg-purple-500/30 transition-colors">
                  <Mail className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800 dark:text-slate-100">Email Support</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Get a response within 24 hours</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-purple-400 transition-colors" />
              </a>
              
              {/* Schedule a Call */}
              <a 
                href="mailto:support@strategicarchitect.net?subject=Schedule%20Support%20Call%20-%20Enterprise&body=Hi%2C%0A%0AI%20would%20like%20to%20schedule%20a%20support%20call.%0A%0APreferred%20times%3A%0A-%20%0A%0ATopic%3A%0A"
                className="flex items-center gap-4 p-4 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-xl border border-purple-500/20 hover:border-purple-500/40 transition-colors group"
                data-testid="priority-support-call"
              >
                <div className="p-3 bg-[#26C6DA]/20 rounded-lg group-hover:bg-[#26C6DA]/30 transition-colors">
                  <Calendar className="w-5 h-5 text-[#26C6DA]" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800 dark:text-slate-100">Schedule a Call</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Book a 1-on-1 support session</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#26C6DA] transition-colors" />
              </a>
            </div>
            
            <div className="text-xs text-slate-500 dark:text-slate-400 pl-4">
              <p><strong>Response Time:</strong> Priority support requests are handled within 24 hours (business days)</p>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-[#1F2937] p-6" data-testid="promo-code-section">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <Ticket className="w-5 h-5 text-[#26C6DA]" />
          Promo Code
        </h2>
        
        {promoInfo ? (
          <div className="p-4 bg-gradient-to-r from-[#26C6DA]/10 to-[#8B5CF6]/10 border border-[#26C6DA]/30 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#26C6DA]/20 rounded-lg">
                <Gift className="w-5 h-5 text-[#26C6DA]" />
              </div>
              <div>
                <p className="font-medium text-slate-800 dark:text-slate-100">
                  Active Promo: <span className="text-[#26C6DA]">{promoInfo.code}</span>
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {promoInfo.plan.charAt(0).toUpperCase() + promoInfo.plan.slice(1)} access until {new Date(promoInfo.expires_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Have a promo code? Enter it below to unlock premium features.
            </p>
            <div className="flex gap-3">
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter promo code"
                className="flex-1 font-mono uppercase bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937]"
                data-testid="promo-code-input"
              />
              <Button
                onClick={handleRedeemPromo}
                disabled={redeemingPromo || !promoCode.trim()}
                className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full px-6"
                data-testid="redeem-promo-btn"
              >
                {redeemingPromo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Redeem'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Billing Period Toggle */}
      <div className="flex flex-col items-center gap-2">
        <div className="inline-flex items-center p-1 bg-slate-100 dark:bg-[#1A1A2E] rounded-full border border-slate-200 dark:border-[#1F2937]">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              billingPeriod === 'monthly'
                ? 'bg-[#26C6DA] text-black'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
            data-testid="billing-monthly-btn"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              billingPeriod === 'annual'
                ? 'bg-[#26C6DA] text-black'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
            data-testid="billing-annual-btn"
          >
            Annual
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              billingPeriod === 'annual' 
                ? 'bg-black/20 text-black' 
                : 'bg-emerald-500/20 text-emerald-400'
            }`}>
              1 Month Free
            </span>
          </button>
        </div>
        {billingPeriod === 'annual' && (
          <p className="text-sm text-emerald-400">Pay for 11 months, get 12 months of access!</p>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {Object.entries(PLAN_FEATURES).map(([key, plan]) => {
          const isCurrent = key === currentPlan;
          const isUpgrade = planOrder[key] > planOrder[currentPlan];
          const isDowngrade = planOrder[key] < planOrder[currentPlan];

          return (
            <div
              key={key}
              className={`relative bg-white dark:bg-[#111827] backdrop-blur-sm rounded-2xl border-2 p-6 transition-all ${
                plan.highlight 
                  ? 'border-[#8B5CF6] shadow-lg shadow-[#8B5CF6]/20' 
                  : isCurrent 
                    ? 'border-[#26C6DA]' 
                    : 'border-slate-200 dark:border-[#1F2937]'
              }`}
              data-testid={`plan-card-${key}`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-gradient-to-r from-[#8B5CF6] to-[#26C6DA] text-white text-xs font-medium rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </span>
                </div>
              )}

              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-[#26C6DA] text-black text-xs font-medium rounded-full">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{plan.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-[#26C6DA]">
                    ${billingPeriod === 'annual' ? plan.annualPrice : plan.price}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    /{billingPeriod === 'annual' ? 'year' : 'month'}
                  </span>
                  {billingPeriod === 'annual' && plan.price > 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      ${(plan.annualPrice / 12).toFixed(0)}/month
                    </p>
                  )}
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-600 flex-shrink-0" />
                    )}
                    <span className={feature.included ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <Button 
                  variant="outline" 
                  className="w-full rounded-full border-slate-200 dark:border-[#1F2937] text-slate-500 dark:text-slate-400" 
                  disabled
                  data-testid={`plan-btn-${key}`}
                >
                  Current Plan
                </Button>
              ) : isUpgrade ? (
                <Button
                  onClick={() => handleUpgrade(key)}
                  disabled={upgrading === key}
                  className="w-full rounded-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium"
                  data-testid={`plan-btn-${key}`}
                >
                  {upgrading === key ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Upgrade to {plan.name}
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full rounded-full text-slate-500 border-slate-200 dark:border-[#1F2937] cursor-not-allowed" 
                  disabled
                  data-testid={`plan-btn-${key}`}
                >
                  Downgrade not available
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Payment History */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-[#1F2937] p-6">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#26C6DA]" />
            Payment History
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-[#1F2937]">
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Plan</th>
                  <th className="pb-3 font-medium">Amount</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx) => (
                  <tr key={tx.id} className="border-b border-slate-200 dark:border-[#1F2937] last:border-0">
                    <td className="py-3 text-sm text-slate-600 dark:text-slate-300">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-sm text-slate-600 dark:text-slate-300 capitalize">{tx.plan_to_upgrade}</td>
                    <td className="py-3 text-sm font-medium text-slate-800 dark:text-slate-100">${tx.amount}</td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        tx.status === 'paid' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : tx.status === 'pending'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-red-500/20 text-red-400'
                      }`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const UsageItem = ({ icon: Icon, label, current, max }) => {
  const isUnlimited = max === -1;
  const percentage = isUnlimited ? 0 : (current / max) * 100;
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && percentage >= 100;

  return (
    <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#26C6DA]" />
        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className="text-xl font-bold text-slate-800 dark:text-slate-100">
        {current} / {isUnlimited ? '∞' : max}
      </div>
      {!isUnlimited && (
        <div className="mt-2 h-1.5 bg-[#1F2937] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-[#26C6DA]'
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default Billing;
